import { useTranslation } from 'react-i18next';
import type { CreditLimitView } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';
import { DataTable, type DataColumn } from '../../components/DataTable.js';
import { cycleWindowStart, formatResetAt } from '../../utils/reset-at.js';

export function subjectLabel(
  limit: CreditLimitView,
  nameOf: (kind: 'group' | 'user', id: string) => string,
  tenantLabel: string
): string {
  if (limit.scope.kind === 'tenant') return tenantLabel;
  if (limit.scope.kind === 'group') return nameOf('group', limit.scope.groupId);
  return nameOf('user', limit.scope.userId);
}

export function CreditLimitTable({
  limits,
  advancedPolicyCount,
  locale,
  busy,
  nameOf,
  onEdit,
  onDelete
}: {
  limits: CreditLimitView[];
  advancedPolicyCount: number;
  locale: string;
  busy: boolean;
  nameOf: (kind: 'group' | 'user', id: string) => string;
  onEdit: (limit: CreditLimitView) => void;
  onDelete: (limit: CreditLimitView) => void;
}) {
  const { t } = useTranslation();
  const tenantLabel = t('creditLimits.subjectTenant');

  const columns: DataColumn<CreditLimitView>[] = [
    {
      key: 'subject',
      header: t('creditLimits.subject'),
      render: (row) => subjectLabel(row, nameOf, tenantLabel),
      sortValue: (row) => subjectLabel(row, nameOf, tenantLabel)
    },
    {
      key: 'cycle',
      header: t('creditLimits.cycle'),
      render: (row) => t(`creditLimits.cycle_${row.cycle}`),
      sortValue: (row) => row.cycle
    },
    {
      key: 'model',
      header: t('creditLimits.model'),
      render: (row) =>
        !row.catalogModelId ? (
          t('creditLimits.modelAll')
        ) : row.orphanedModel ? (
          // Kept, not deleted: re-assigning the model makes the limit live again.
          <span className="warning" title={t('creditLimits.modelOrphanedHint')}>
            {t('creditLimits.modelOrphaned', { model: row.catalogModelId })}
          </span>
        ) : (
          row.catalogModelId
        ),
      sortValue: (row) => row.catalogModelId ?? ''
    },
    {
      key: 'amount',
      header: t('creditLimits.amount'),
      render: (row) => toCredits(row.creditMillis),
      sortValue: (row) => row.creditMillis
    },
    {
      key: 'behavior',
      header: t('creditLimits.behavior'),
      render: (row) => t(`creditLimits.behavior_${row.overageBehavior}`),
      sortValue: (row) => row.overageBehavior
    },
    {
      key: 'usage',
      header: t('creditLimits.usage'),
      render: (row) => {
        const remaining = row.remainingMillis ?? 0;
        const text = t('creditLimits.consumedOfLimit', {
          consumed: toCredits(row.consumedMillis ?? 0),
          remaining: toCredits(remaining)
        });
        // Never colour alone: the flag and the title carry the same signal.
        return remaining <= 0 ? (
          <span className="warning" title={t('creditLimits.exhausted')}>
            {`⚑ ${text}`}
          </span>
        ) : (
          text
        );
      },
      sortValue: (row) => row.remainingMillis ?? 0
    },
    {
      key: 'cycleStart',
      header: t('creditLimits.cycleStart'),
      // The usage column counts from here, not from when the limit was created:
      // a limit added mid-window inherits the spend already in it.
      render: (row) =>
        row.cycle === 'lifetime'
          ? t('creditLimits.lifetimeNoStart')
          : (formatResetAt(cycleWindowStart(row.cycle, row.resetAt), locale) ?? '—'),
      sortValue: (row) => cycleWindowStart(row.cycle, row.resetAt) ?? ''
    },
    {
      key: 'resetAt',
      header: t('creditLimits.resetAt'),
      render: (row) =>
        row.cycle === 'lifetime'
          ? t('creditLimits.lifetimeNoReset')
          : (formatResetAt(row.resetAt, locale) ?? '—'),
      sortValue: (row) => row.resetAt ?? ''
    },
    {
      key: 'actions',
      header: t('creditLimits.actions'),
      render: (row) => (
        <span className="row-actions">
          <button type="button" onClick={() => onEdit(row)} disabled={busy}>
            {t('creditLimits.edit')}
          </button>
          <button type="button" onClick={() => onDelete(row)} disabled={busy}>
            {t('creditLimits.delete')}
          </button>
        </span>
      )
    }
  ];

  return (
    <section className="panel">
      <h2>{t('creditLimits.listTitle', { count: limits.length })}</h2>
      <DataTable
        rows={limits}
        columns={columns}
        empty={t('creditLimits.emptyState')}
        getRowKey={(row) => row.id}
        defaultSort={{ key: 'subject', dir: 'asc' }}
      />
      {advancedPolicyCount > 0 ? (
        <p className="muted">
          {t('creditLimits.advancedPolicyNote', { count: advancedPolicyCount })}
        </p>
      ) : null}
    </section>
  );
}
